import { adoszamEllenorzes, cegjegyzekszamNormalizalas, evSzamEllenorzes } from '@ertekbecslo/kozos/ceg';
import { huMobilNormalizalas } from '@ertekbecslo/kozos/telefon';
import type { JogiAllapot } from './jogi.ts';

export type Halozat = 'duna_house' | 'otthon_centrum' | 'egyeb' | 'fuggetlen';
export type CegForma = 'tarsasag' | 'egyeni_vallalkozo';

/** Az űrlap szöveges állapota; az elfogadások külön (`Elfogadasok`), mert nem kerülnek az adatsorba. */
export interface RegisztracioUrlapAdat {
  irodaNev: string;
  /** Üres: még nem választott. */
  halozat: Halozat | '';
  halozatEgyeb: string;
  telepules: string;
  regio: string;
  irodaCim: string;
  cegForma: CegForma;
  cegnev: string;
  szekhely: string;
  adoszam: string;
  cegjegyzekszam: string;
  evNyilvantartasiSzam: string;
  szamlazasiEmail: string;
  kapcsolattartoNev: string;
  kapcsolattartoEmail: string;
  kapcsolattartoTelefon: string;
  megjegyzes: string;
}

export interface Elfogadasok { aszf: boolean; adatvedelem: boolean; kepviselet: boolean; halozati: boolean }

/** A `iroda-regisztracio` Edge Function kérés-törzse (supabase/functions/_shared/regisztracio.ts, IrodaRegisztracioKeres). */
export interface IrodaRegisztracioKeres {
  irodaNev: string;
  halozat: Halozat;
  halozatEgyeb?: string;
  telepules: string;
  regio: string;
  irodaCim?: string;
  cegForma: CegForma;
  cegnev: string;
  szekhely: string;
  adoszam: string;
  cegjegyzekszam?: string;
  evNyilvantartasiSzam?: string;
  szamlazasiEmail: string;
  kapcsolattartoNev: string;
  kapcsolattartoEmail: string;
  kapcsolattartoTelefon: string;
  megjegyzes?: string;
  elfogadas: { aszfVerzio: string; adatvedelemVerzio: string; aszf: true; adatvedelem: true; kepviselet: true; halozati?: boolean };
  turnstileToken: string;
}

export const HIBA_SZOVEGEK: Record<'ervenytelen' | 'turnstile' | 'korlat' | 'email' | 'adoszam' | 'cegszam' | 'telefon' | 'jogi_verzio' | 'kikapcsolva' | 'szerver' | 'halozat', string> = {
  ervenytelen: 'Az űrlap adatai érvénytelenek. Ellenőrizze a megadott adatokat, és próbálja újra.',
  turnstile: 'Nem sikerült az automatikus ellenőrzés. Töltse újra az oldalt, és próbálja újra.',
  korlat: 'Túl sok kísérlet történt. Kérjük, próbálja újra később.',
  email: 'Erre az e-mail-címre nem tudunk levelet küldeni. Adjon meg egy másik, működő címet.',
  adoszam: 'Az adószám érvénytelen. Ellenőrizze a megadott számot.',
  cegszam: 'A cégjegyzékszám vagy a nyilvántartási szám formátuma hibás.',
  telefon: 'A telefonszám nem érvényes magyar mobilszám.',
  jogi_verzio: 'A feltételek közben frissültek. Töltse újra az oldalt, és olvassa el az új változatot.',
  kikapcsolva: 'Az online regisztráció jelenleg nem érhető el.',
  szerver: 'Szerverhiba történt. Kérjük, próbálja újra később.',
  halozat: 'Nincs kapcsolat a szerverrel. Ellenőrizze az internetet, és próbálja újra.',
};

export function regisztracioUrlapUres(): RegisztracioUrlapAdat {
  return {
    irodaNev: '', halozat: '', halozatEgyeb: '', telepules: '', regio: '', irodaCim: '',
    cegForma: 'tarsasag', cegnev: '', szekhely: '', adoszam: '', cegjegyzekszam: '', evNyilvantartasiSzam: '',
    szamlazasiEmail: '', kapcsolattartoNev: '', kapcsolattartoEmail: '', kapcsolattartoTelefon: '', megjegyzes: '',
  };
}

export const elfogadasokUresen = (): Elfogadasok => ({ aszf: false, adatvedelem: false, kepviselet: false, halozati: false });

/** A Duna House és az Otthon Centrum esetén hálózati nyilatkozat kell (ÁSZF 4.6.). */
export const nagyHalozat = (h: Halozat | ''): boolean => h === 'duna_house' || h === 'otthon_centrum';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function hosszHiba(ertek: string, nev: string, min: number, max: number): string | undefined {
  const s = ertek.trim();
  if (s === '') return `${nev} megadása kötelező.`;
  if (s.length < min) return `${nev} legalább ${min} karakter legyen.`;
  if (s.length > max) return `Legfeljebb ${max} karakter lehet.`;
  return undefined;
}

export function regisztracioHibak(a: RegisztracioUrlapAdat, e: Elfogadasok): Partial<Record<string, string>> {
  const h: Partial<Record<string, string>> = {};
  const be = (kulcs: string, hiba: string | undefined) => { if (hiba) h[kulcs] = hiba; };

  be('irodaNev', hosszHiba(a.irodaNev, 'Az iroda neve', 2, 120));
  if (a.halozat === '') h.halozat = 'Válassza ki az iroda hálózatát.';
  if (a.halozat === 'egyeb') be('halozatEgyeb', hosszHiba(a.halozatEgyeb, 'A hálózat neve', 1, 120));
  be('telepules', hosszHiba(a.telepules, 'A település', 1, 120));
  be('regio', hosszHiba(a.regio, 'A régió', 1, 120));
  if (a.irodaCim.trim().length > 200) h.irodaCim = 'Legfeljebb 200 karakter lehet.';

  be('cegnev', hosszHiba(a.cegnev, 'A cégnév', 2, 200));
  be('szekhely', hosszHiba(a.szekhely, 'A székhely', 5, 300));
  const adoszam = a.adoszam.trim();
  if (adoszam === '') h.adoszam = 'Az adószám megadása kötelező.';
  else if (!/^\d{11}$/.test(adoszam.replace(/[\s-]/g, ''))) h.adoszam = 'Az adószám formátuma: 12345678-1-23.';
  else if (!adoszamEllenorzes(adoszam).ok) h.adoszam = 'Az adószám ellenőrző jegye hibás.';
  if (a.cegForma === 'tarsasag') {
    if (a.cegjegyzekszam.trim() === '') h.cegjegyzekszam = 'A cégjegyzékszám megadása kötelező.';
    else if (!cegjegyzekszamNormalizalas(a.cegjegyzekszam)) h.cegjegyzekszam = 'A cégjegyzékszám formátuma: 01-09-123456.';
  } else {
    if (a.evNyilvantartasiSzam.trim() === '') h.evNyilvantartasiSzam = 'A nyilvántartási szám megadása kötelező.';
    else if (!evSzamEllenorzes(a.evNyilvantartasiSzam)) h.evNyilvantartasiSzam = 'A nyilvántartási szám 8 számjegyből áll.';
  }
  const szamlazas = a.szamlazasiEmail.trim();
  if (szamlazas === '') h.szamlazasiEmail = 'A számlázási e-mail-cím megadása kötelező.';
  else if (!EMAIL.test(szamlazas) || szamlazas.length > 200) h.szamlazasiEmail = 'Ez nem érvényes e-mail-cím.';

  be('kapcsolattartoNev', hosszHiba(a.kapcsolattartoNev, 'A kapcsolattartó neve', 2, 120));
  const email = a.kapcsolattartoEmail.trim();
  if (email === '') h.kapcsolattartoEmail = 'A kapcsolattartó e-mail-címe kötelező.';
  else if (!EMAIL.test(email) || email.length > 200) h.kapcsolattartoEmail = 'Ez nem érvényes e-mail-cím.';
  const tel = a.kapcsolattartoTelefon.trim();
  if (tel === '') h.kapcsolattartoTelefon = 'A kapcsolattartó mobilszáma kötelező.';
  else if (!huMobilNormalizalas(tel).ok) h.kapcsolattartoTelefon = 'Csak magyar mobilszám adható meg (pl. +36 30 123 4567).';

  if (a.megjegyzes.trim().length > 1000) h.megjegyzes = 'Legfeljebb 1000 karakter lehet.';

  if (!e.aszf) h.aszf = 'A regisztrációhoz el kell fogadnia a Partner ÁSZF-et.';
  if (!e.adatvedelem) h.adatvedelem = 'Az Adatvédelmi tájékoztató megismerését jelezni kell.';
  if (!e.kepviselet) h.kepviselet = 'A képviseleti jogosultságról nyilatkoznia kell.';
  if (nagyHalozat(a.halozat) && !e.halozati) h.halozati = 'A hálózati tagságról nyilatkoznia kell.';
  return h;
}

const nemUres = (s: string): string | undefined => (s.trim() === '' ? undefined : s.trim());

/**
 * A kérés-törzs. Csak a sémában szereplő kulcsokat adja (a háttér `.strict()`), és a cégformához nem tartozó
 * mezőt nem küldi. Az adószám, a cégjegyzékszám és a telefon nyers szöveg: a normalizálás a szerveré.
 * Az elfogadások igazak voltát a `regisztracioHibak` előzi meg.
 */
export function regisztracioKeres(a: RegisztracioUrlapAdat, turnstileToken: string, j: JogiAllapot): IrodaRegisztracioKeres {
  if (a.halozat === '') throw new Error('halozat_nincs_valasztva');
  const k: IrodaRegisztracioKeres = {
    irodaNev: a.irodaNev.trim(),
    halozat: a.halozat,
    telepules: a.telepules.trim(),
    regio: a.regio.trim(),
    cegForma: a.cegForma,
    cegnev: a.cegnev.trim(),
    szekhely: a.szekhely.trim(),
    adoszam: a.adoszam.trim(),
    szamlazasiEmail: a.szamlazasiEmail.trim(),
    kapcsolattartoNev: a.kapcsolattartoNev.trim(),
    kapcsolattartoEmail: a.kapcsolattartoEmail.trim(),
    kapcsolattartoTelefon: a.kapcsolattartoTelefon.trim(),
    elfogadas: {
      aszfVerzio: j.aszf.verzio, adatvedelemVerzio: j.adatvedelem.verzio, aszf: true, adatvedelem: true, kepviselet: true,
      ...(nagyHalozat(a.halozat) ? { halozati: true } : {}),
    },
    turnstileToken,
  };
  if (a.halozat === 'egyeb') k.halozatEgyeb = a.halozatEgyeb.trim();
  const cim = nemUres(a.irodaCim);
  if (cim) k.irodaCim = cim;
  if (a.cegForma === 'tarsasag') k.cegjegyzekszam = a.cegjegyzekszam.trim();
  else k.evNyilvantartasiSzam = a.evNyilvantartasiSzam.trim();
  const megjegyzes = nemUres(a.megjegyzes);
  if (megjegyzes) k.megjegyzes = megjegyzes;
  return k;
}
